/**
 * Image Quality Assessment Service
 *
 * Provides image quality analysis including:
 * - Laplacian variance blur detection
 * - Brightness validation
 * - Contrast assessment
 * - Glare detection
 */

import { createScopedReporter } from './errorReporter';

const SERVICE_TAG = '[IMAGE_QUALITY]';
const reporter = createScopedReporter('IMAGE');

/** Quality thresholds for image validation */
export const QUALITY_THRESHOLDS = {
  /** Below this Laplacian variance = too blurry */
  BLUR_LAPLACIAN_MIN: 100,
  /** Below this brightness (0-255) = too dark */
  BRIGHTNESS_MIN: 50,
  /** Above this brightness = too bright */
  BRIGHTNESS_MAX: 200,
  /** Below this contrast (std dev) = low contrast */
  CONTRAST_STDDEV_MIN: 30,
  /** Percentage of over-exposed pixels indicating glare */
  GLARE_THRESHOLD: 0.15,
  /** Pixel value threshold for considering a pixel over-exposed */
  GLARE_PIXEL_THRESHOLD: 250
};

/**
 * Quality assessment result for a single metric
 */
export interface BlurAssessment {
  /** Laplacian variance score (higher = sharper) */
  score: number;
  /** Whether blur is acceptable for capture */
  acceptable: boolean;
}

/**
 * Brightness assessment result
 */
export interface BrightnessAssessment {
  /** Average brightness value 0-255 */
  value: number;
  /** Whether brightness is acceptable */
  acceptable: boolean;
  /** Reason for rejection if not acceptable */
  reason?: 'too_dark' | 'too_bright';
}

/**
 * Contrast assessment result
 */
export interface ContrastAssessment {
  /** Standard deviation of brightness */
  stdDev: number;
  /** Whether contrast is acceptable */
  acceptable: boolean;
}

/**
 * Glare detection result
 */
export interface GlareAssessment {
  /** Percentage of over-exposed pixels */
  percentage: number;
  /** Whether significant glare was detected */
  hasGlare: boolean;
}

/**
 * Complete quality assessment result
 */
export interface QualityAssessment {
  /** Whether image passes all blocking quality gates */
  isAcceptable: boolean;
  /** Blur assessment */
  blur: BlurAssessment;
  /** Brightness assessment */
  brightness: BrightnessAssessment;
  /** Contrast assessment */
  contrast: ContrastAssessment;
  /** Glare detection (warning only, not blocking) */
  glare: GlareAssessment;
  /** Overall quality score 0-1 */
  overallScore: number;
  /** Reasons why image was rejected (empty if acceptable) */
  rejectionReasons: string[];
  /** Non-blocking warnings */
  warnings: string[];
}

/**
 * 3x3 Laplacian kernel for edge detection
 * Sum = 0, center = 8, surroundings = -1
 */
const LAPLACIAN_KERNEL = [
  [-1, -1, -1],
  [-1, 8, -1],
  [-1, -1, -1]
];

/**
 * Convert RGBA pixel data to grayscale array
 *
 * @param imageData - ImageData from canvas
 * @returns Uint8Array of grayscale values
 */
function toGrayscale(imageData: ImageData): Uint8Array {
  const { data, width, height } = imageData;
  const grayscale = new Uint8Array(width * height);

  for (let i = 0; i < data.length; i += 4) {
    // Standard luminance formula
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    grayscale[i / 4] = Math.round(gray);
  }

  return grayscale;
}

/**
 * Calculate Laplacian variance for blur detection
 *
 * The Laplacian operator highlights regions of rapid intensity change (edges).
 * Blurry images have fewer edges, resulting in lower variance of the Laplacian.
 *
 * @param imageData - ImageData from canvas
 * @returns Laplacian variance (< 100 = blurry, > 500 = very sharp)
 */
export function calculateLaplacianVariance(imageData: ImageData): number {
  const { width, height } = imageData;
  const grayscale = toGrayscale(imageData);

  // Apply Laplacian kernel and collect results
  const laplacianValues: number[] = [];

  // Skip border pixels (kernel needs 1px padding)
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      let sum = 0;

      // Apply 3x3 kernel
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const pixelIndex = (y + ky) * width + (x + kx);
          const kernelValue = LAPLACIAN_KERNEL[ky + 1][kx + 1];
          sum += grayscale[pixelIndex] * kernelValue;
        }
      }

      laplacianValues.push(sum);
    }
  }

  // Calculate variance of Laplacian values
  if (laplacianValues.length === 0) {
    reporter.warning('No Laplacian values calculated, image too small', {
      context: { width, height, fallbackValue: 0 },
      showToast: false
    });
    return 0;
  }

  const mean = laplacianValues.reduce((a, b) => a + b, 0) / laplacianValues.length;
  const variance =
    laplacianValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
    laplacianValues.length;

  return variance;
}

/**
 * Calculate average brightness of image
 *
 * @param imageData - ImageData from canvas
 * @returns Average brightness 0-255
 */
function calculateBrightness(imageData: ImageData): number {
  const { data } = imageData;
  let totalBrightness = 0;
  const pixelCount = data.length / 4;

  for (let i = 0; i < data.length; i += 4) {
    // Standard luminance formula
    const luminance = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    totalBrightness += luminance;
  }

  return totalBrightness / pixelCount;
}

/**
 * Calculate contrast as standard deviation of brightness
 *
 * @param imageData - ImageData from canvas
 * @param avgBrightness - Pre-calculated average brightness
 * @returns Standard deviation of brightness
 */
function calculateContrast(imageData: ImageData, avgBrightness: number): number {
  const { data } = imageData;
  const pixelCount = data.length / 4;
  let varianceSum = 0;

  for (let i = 0; i < data.length; i += 4) {
    const luminance = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    varianceSum += Math.pow(luminance - avgBrightness, 2);
  }

  return Math.sqrt(varianceSum / pixelCount);
}

/**
 * Calculate percentage of over-exposed (glare) pixels
 *
 * @param imageData - ImageData from canvas
 * @returns Percentage of pixels above glare threshold
 */
function calculateGlarePercentage(imageData: ImageData): number {
  const { data } = imageData;
  const pixelCount = data.length / 4;
  let glarePixels = 0;

  for (let i = 0; i < data.length; i += 4) {
    // Check if any RGB channel is over-exposed
    if (
      data[i] >= QUALITY_THRESHOLDS.GLARE_PIXEL_THRESHOLD ||
      data[i + 1] >= QUALITY_THRESHOLDS.GLARE_PIXEL_THRESHOLD ||
      data[i + 2] >= QUALITY_THRESHOLDS.GLARE_PIXEL_THRESHOLD
    ) {
      glarePixels++;
    }
  }

  return glarePixels / pixelCount;
}

/**
 * Full quality assessment of an image
 *
 * Analyzes blur, brightness, contrast, and glare to determine
 * if the image is suitable for dice recognition.
 *
 * @param imageData - ImageData from canvas
 * @returns Complete quality assessment with scores and pass/fail status
 */
export function assessImageQuality(imageData: ImageData): QualityAssessment {
  const rejectionReasons: string[] = [];
  const warnings: string[] = [];

  // Blur assessment via Laplacian variance
  const blurScore = calculateLaplacianVariance(imageData);
  const blurAcceptable = blurScore >= QUALITY_THRESHOLDS.BLUR_LAPLACIAN_MIN;

  if (!blurAcceptable) {
    rejectionReasons.push('Image is too blurry - hold the camera steady');
    console.warn(`${SERVICE_TAG} Blur check failed`, {
      laplacianVariance: blurScore.toFixed(2),
      threshold: QUALITY_THRESHOLDS.BLUR_LAPLACIAN_MIN
    });
  }

  // Brightness assessment
  const brightnessValue = calculateBrightness(imageData);
  let brightnessAcceptable = true;
  let brightnessReason: 'too_dark' | 'too_bright' | undefined;

  if (brightnessValue < QUALITY_THRESHOLDS.BRIGHTNESS_MIN) {
    brightnessAcceptable = false;
    brightnessReason = 'too_dark';
    rejectionReasons.push('Image is too dark - increase lighting');
    console.warn(`${SERVICE_TAG} Brightness check failed - too dark`, {
      brightness: brightnessValue.toFixed(2),
      minThreshold: QUALITY_THRESHOLDS.BRIGHTNESS_MIN
    });
  } else if (brightnessValue > QUALITY_THRESHOLDS.BRIGHTNESS_MAX) {
    brightnessAcceptable = false;
    brightnessReason = 'too_bright';
    rejectionReasons.push('Image is too bright - reduce lighting');
    console.warn(`${SERVICE_TAG} Brightness check failed - too bright`, {
      brightness: brightnessValue.toFixed(2),
      maxThreshold: QUALITY_THRESHOLDS.BRIGHTNESS_MAX
    });
  }

  // Contrast assessment
  const contrastStdDev = calculateContrast(imageData, brightnessValue);
  const contrastAcceptable = contrastStdDev >= QUALITY_THRESHOLDS.CONTRAST_STDDEV_MIN;

  if (!contrastAcceptable) {
    rejectionReasons.push('Low contrast - ensure dice is visible against background');
    console.warn(`${SERVICE_TAG} Contrast check failed`, {
      contrastStdDev: contrastStdDev.toFixed(2),
      threshold: QUALITY_THRESHOLDS.CONTRAST_STDDEV_MIN
    });
  }

  // Glare detection (warning only, not blocking)
  const glarePercentage = calculateGlarePercentage(imageData);
  const hasGlare = glarePercentage >= QUALITY_THRESHOLDS.GLARE_THRESHOLD;

  if (hasGlare) {
    warnings.push('Glare detected - consider adjusting lighting angle');
    console.info(`${SERVICE_TAG} Glare warning`, {
      glarePercentage: (glarePercentage * 100).toFixed(1) + '%',
      threshold: (QUALITY_THRESHOLDS.GLARE_THRESHOLD * 100).toFixed(1) + '%'
    });
  }

  // Calculate overall quality score (0-1)
  // Weighted combination: blur 40%, brightness 25%, contrast 35%
  const blurNormalized = Math.min(blurScore / 500, 1); // Normalize to 0-1, cap at 500
  const brightnessNormalized =
    1 - Math.abs(brightnessValue - 128) / 128; // Optimal at 128
  const contrastNormalized = Math.min(contrastStdDev / 60, 1); // Normalize to 0-1

  const overallScore = blurNormalized * 0.4 + brightnessNormalized * 0.25 + contrastNormalized * 0.35;

  // Image is acceptable if all blocking checks pass
  const isAcceptable = blurAcceptable && brightnessAcceptable && contrastAcceptable;

  const assessment: QualityAssessment = {
    isAcceptable,
    blur: { score: blurScore, acceptable: blurAcceptable },
    brightness: {
      value: brightnessValue,
      acceptable: brightnessAcceptable,
      ...(brightnessReason && { reason: brightnessReason })
    },
    contrast: { stdDev: contrastStdDev, acceptable: contrastAcceptable },
    glare: { percentage: glarePercentage, hasGlare },
    overallScore: Math.max(0, Math.min(1, overallScore)),
    rejectionReasons,
    warnings
  };

  console.debug(`${SERVICE_TAG} Quality assessment complete`, {
    isAcceptable,
    overallScore: overallScore.toFixed(2),
    blur: blurScore.toFixed(2),
    brightness: brightnessValue.toFixed(2),
    contrast: contrastStdDev.toFixed(2),
    glare: (glarePercentage * 100).toFixed(1) + '%',
    rejectionCount: rejectionReasons.length,
    warningCount: warnings.length
  });

  return assessment;
}

/**
 * Get ImageData from a video element
 *
 * @param videoElement - Video element to capture from
 * @param sampleSize - Size of the sample canvas (default 256 for performance)
 * @returns ImageData or null if unable to capture
 */
export function getImageDataFromVideo(
  videoElement: HTMLVideoElement,
  sampleSize = 256
): ImageData | null {
  const canvas = document.createElement('canvas');
  canvas.width = sampleSize;
  canvas.height = sampleSize;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    reporter.error('Failed to get canvas context for image data', { showToast: false });
    return null;
  }

  ctx.drawImage(videoElement, 0, 0, sampleSize, sampleSize);
  return ctx.getImageData(0, 0, sampleSize, sampleSize);
}

/**
 * Assess quality of a video frame
 *
 * Convenience function that captures ImageData from video and runs assessment.
 *
 * @param videoElement - Video element to assess
 * @returns QualityAssessment or null if unable to capture
 */
export function assessCaptureQuality(videoElement: HTMLVideoElement): QualityAssessment | null {
  const imageData = getImageDataFromVideo(videoElement);

  if (!imageData) {
    reporter.error('Cannot assess quality - failed to get image data', { showToast: false });
    return null;
  }

  return assessImageQuality(imageData);
}
